import { NgClass, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, NgZone, OnDestroy, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule } from '@ngneat/transloco';
import { NotificationToastService } from 'app/layout/common/notification-toast/notification-toast.service';
import { NotificationToast } from 'app/layout/common/notification-toast/notification-toast.types';

/** Countdown bookkeeping for one toast so hover/focus can pause it. */
interface ToastCountdown {
    handle: ReturnType<typeof setTimeout> | null;
    /** Timestamp the current run started at, or null while paused. */
    startedAt: number | null;
    /** Milliseconds still to run when resumed. */
    remaining: number;
}

/**
 * The live toast layer: a stack of transient notification cards pinned to the
 * top inline-end corner, above the page and below every CDK overlay.
 *
 * Hosted once by `LayoutComponent` so it is present for every layout variant.
 * It renders nothing until `NotificationToastService` has something to show.
 */
@Component({
    selector: 'notification-toast',
    templateUrl: './notification-toast.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [NgIf, NgFor, NgClass, MatButtonModule, MatIconModule, MatTooltipModule, TranslocoModule],
})
export class NotificationToastComponent implements OnDestroy {
    /** Must match the `.sai-toast--leaving` animation duration (--sai-duration-base). */
    private static readonly EXIT_MS = 200;

    toasts: NotificationToast[] = [];

    private readonly _toastService = inject(NotificationToastService);
    private readonly _changeDetectorRef = inject(ChangeDetectorRef);
    private readonly _ngZone = inject(NgZone);
    private readonly _countdowns = new Map<string, ToastCountdown>();
    private readonly _exits = new Map<string, ReturnType<typeof setTimeout>>();
    private readonly _leaving = new Set<string>();

    constructor() {
        this._toastService.toasts$
            .pipe(takeUntilDestroyed())
            .subscribe((toasts: NotificationToast[]) => {
                this.toasts = toasts;
                this._syncCountdowns(toasts);
                this._changeDetectorRef.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this._countdowns.forEach(countdown => this._stopCountdown(countdown));
        this._countdowns.clear();
        this._exits.forEach(handle => clearTimeout(handle));
        this._exits.clear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /** Open the notification the toast stands for, then animate the card away. */
    activate(toast: NotificationToast): void {
        if (this._leaving.has(toast.id)) {
            return;
        }
        this._toastService.activate(toast);
        this.dismiss(toast);
    }

    /** Start the exit animation; the toast leaves the stack when it finishes. */
    dismiss(toast: NotificationToast): void {
        if (this._leaving.has(toast.id)) {
            return;
        }

        const countdown = this._countdowns.get(toast.id);
        if (countdown) {
            this._stopCountdown(countdown);
        }

        this._leaving.add(toast.id);
        this._changeDetectorRef.markForCheck();

        this._exits.set(
            toast.id,
            setTimeout(() => {
                this._exits.delete(toast.id);
                this._leaving.delete(toast.id);
                this._toastService.dismiss(toast.id);
            }, NotificationToastComponent.EXIT_MS),
        );
    }

    /** Hold the countdown while the pointer or keyboard focus rests on a toast. */
    pause(toast: NotificationToast): void {
        const countdown = this._countdowns.get(toast.id);
        if (!countdown || countdown.startedAt === null) {
            return;
        }

        countdown.remaining = Math.max(0, countdown.remaining - (Date.now() - countdown.startedAt));
        this._stopCountdown(countdown);
        this._changeDetectorRef.markForCheck();
    }

    /** Resume a held countdown with whatever time it had left. */
    resume(toast: NotificationToast): void {
        const countdown = this._countdowns.get(toast.id);
        if (!countdown || countdown.startedAt !== null || this._leaving.has(toast.id)) {
            return;
        }

        this._runCountdown(toast, countdown);
        this._changeDetectorRef.markForCheck();
    }

    isLeaving(toast: NotificationToast): boolean {
        return this._leaving.has(toast.id);
    }

    isPaused(toast: NotificationToast): boolean {
        return this._countdowns.get(toast.id)?.startedAt === null;
    }

    trackByToast(index: number, toast: NotificationToast): string {
        return toast.id;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /** Give every visible toast a countdown and drop the ones that left. */
    private _syncCountdowns(toasts: NotificationToast[]): void {
        const visible = new Set(toasts.map(toast => toast.id));

        this._countdowns.forEach((countdown, id) => {
            if (!visible.has(id)) {
                this._stopCountdown(countdown);
                this._countdowns.delete(id);
            }
        });

        toasts.forEach((toast) => {
            if (this._countdowns.has(toast.id)) {
                return;
            }
            const countdown: ToastCountdown = { handle: null, startedAt: null, remaining: toast.duration };
            this._countdowns.set(toast.id, countdown);
            this._runCountdown(toast, countdown);
        });
    }

    /**
     * Schedule the auto-dismiss outside Angular - a toast sitting on screen must
     * not keep change detection ticking for the whole app.
     */
    private _runCountdown(toast: NotificationToast, countdown: ToastCountdown): void {
        countdown.startedAt = Date.now();
        this._ngZone.runOutsideAngular(() => {
            countdown.handle = setTimeout(() => {
                this._ngZone.run(() => this.dismiss(toast));
            }, countdown.remaining);
        });
    }

    private _stopCountdown(countdown: ToastCountdown): void {
        if (countdown.handle !== null) {
            clearTimeout(countdown.handle);
            countdown.handle = null;
        }
        countdown.startedAt = null;
    }
}
